import React from 'react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HTip, HBold, HWarning } from '../HandbookContent';

export default function ChapterGuardsValidators() {
  return (
    <HChapter
      title="Guards & Validators"
      subtitle="The safety checks that ensure a matter is ready before it moves to the next step."
    >
      <HMetaphor
        icon={<ShieldCheckIcon className="w-5 h-5 text-lex-accent" />}
        title="Tollbooths and Checkpoints"
      >
        Before you can take an on-ramp (transition), you may need to pass through a tollbooth (guard)
        and a checkpoint (validator). The tollbooth checks whether you're <em>allowed</em> to proceed —
        do you have the right role? Is the deadline still open? The checkpoint inspects your
        cargo — are all required fields filled in? Is the document properly formatted? Both must
        clear before you can proceed.
      </HMetaphor>

      <HSection title="Guards: Who Can Proceed?">
        <HParagraph>
          A <HBold>guard</HBold> is a pre-condition that must be true before a transition can happen.
          Guards answer the question: <em>"Is this move currently allowed?"</em>
        </HParagraph>
        <HParagraph>Common guard types include:</HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>Role-based guards</HBold> — Only users with specific roles (e.g., "Partner" or
            "Senior Associate") can trigger this transition.
          </li>
          <li>
            <HBold>Field-presence guards</HBold> — A certain field must be filled in on the matter
            before this transition is available. Example: "Case number must exist before filing."
          </li>
          <li>
            <HBold>Time-based guards</HBold> — The matter must have been in the current status for
            a minimum amount of time. Example: "Must wait 10 days after service before moving to default."
          </li>
          <li>
            <HBold>Approval guards</HBold> — A supervisor or partner must approve before the transition
            can proceed. The system collects approvals from designated reviewers.
          </li>
        </ul>
        <HParagraph>
          When a guard fails, the transition button appears <HBold>grayed out</HBold> with a tooltip
          explaining why it's blocked. The user can see exactly what needs to happen before they can proceed.
        </HParagraph>
      </HSection>

      <HSection title="Validators: Is the Data Complete?">
        <HParagraph>
          A <HBold>validator</HBold> runs during the transition (after the user clicks "Go") and checks
          that all required data is present and properly formatted. Validators answer the question:
          <em> "Is everything in order for this move?"</em>
        </HParagraph>
        <HParagraph>
          If a validator fails, the transition is <HBold>rolled back</HBold> — the matter stays at its
          current status and the user sees an error message explaining what needs to be fixed.
        </HParagraph>
        <HParagraph>Common validator types include:</HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>Required fields</HBold> — Specific form fields must be filled in during the transition.
          </li>
          <li>
            <HBold>Document checks</HBold> — A particular document type must be attached to the matter.
          </li>
          <li>
            <HBold>Custom rules</HBold> — Business logic specific to your firm's workflow.
          </li>
        </ul>
      </HSection>

      <HSection title="Guards vs. Validators: What's the Difference?">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-medium" />
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-medium">Guard</th>
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-medium">Validator</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              <tr className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-3 py-2 font-medium">When it runs</td>
                <td className="px-3 py-2">Before the button appears</td>
                <td className="px-3 py-2">After the user clicks</td>
              </tr>
              <tr className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-3 py-2 font-medium">What happens if it fails</td>
                <td className="px-3 py-2">Button is grayed out</td>
                <td className="px-3 py-2">Error message, rolled back</td>
              </tr>
              <tr className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-3 py-2 font-medium">Analogy</td>
                <td className="px-3 py-2">Tollbooth: "Can you enter?"</td>
                <td className="px-3 py-2">Checkpoint: "Is your cargo in order?"</td>
              </tr>
            </tbody>
          </table>
        </div>
      </HSection>

      <HWarning>
        Administrators can force a transition even when guards fail. This is an emergency override
        and should only be used when genuinely necessary. All forced transitions are logged in the
        audit trail.
      </HWarning>

      <HTip>
        When designing your playbook, use guards for conditions the user can't control (role requirements,
        time constraints) and validators for conditions they can fix (missing fields, incomplete documents).
      </HTip>
    </HChapter>
  );
}
